import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Bell, Check, CheckCheck, CalendarDays, CreditCard, XCircle, Clock, Users, Loader2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

const typeIcons = {
  BOOKING_CREATED: CalendarDays, BOOKING_CONFIRMED: Check, BOOKING_CANCELLED: XCircle,
  CASH_CONFIRMED: CreditCard, PAYMENT_RECEIVED: CreditCard, BOOKING_REMINDER: Clock,
  NEW_BOOKING_PROVIDER: Users,
};
const typeColors = {
  BOOKING_CREATED: "text-blue-500", BOOKING_CONFIRMED: "text-lime-volt", BOOKING_CANCELLED: "text-red-500",
  CASH_CONFIRMED: "text-green-500", PAYMENT_RECEIVED: "text-green-500", BOOKING_REMINDER: "text-amber-500",
  NEW_BOOKING_PROVIDER: "text-purple-500",
};

export default function NotificationBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const pollRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get("/notifications/unread-count");
      setUnreadCount(res.data.count);
    } catch {}
  }, [user]);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await api.get("/notifications", { params: { limit: 20 } });
      setNotifications(res.data);
    } catch {}
    setLoading(false);
  }, [user]);

  // Poll unread count every 30s
  useEffect(() => {
    if (!user) return;
    fetchUnreadCount();
    pollRef.current = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(pollRef.current);
  }, [user, fetchUnreadCount]);

  // Fetch full list when opening
  useEffect(() => {
    if (open) { fetchNotifications(); fetchUnreadCount(); }
  }, [open, fetchNotifications, fetchUnreadCount]);

  const markRead = async (notifId) => {
    try {
      await api.put(`/notifications/${notifId}/read`);
      setNotifications(n => n.map(x => x.notification_id === notifId ? { ...x, is_read: true } : x));
      setUnreadCount(c => Math.max(0, c - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await api.put("/notifications/read-all");
      setNotifications(n => n.map(x => ({ ...x, is_read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const handleClick = (notif) => {
    if (!notif.is_read) markRead(notif.notification_id);
    if (notif.data?.booking_id) { setOpen(false); navigate("/bookings"); }
  };

  const formatTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString("en-PH", { month: "short", day: "numeric" });
  };

  if (!user) return null;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button data-testid="notification-bell" variant="ghost" className="relative rounded-full w-9 h-9 p-0">
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-court-orange text-white text-[10px] font-bold rounded-full flex items-center justify-center"
              data-testid="unread-badge">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 md:w-96 p-0" data-testid="notification-dropdown">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <h3 className="font-heading text-sm uppercase tracking-[0.15em] font-bold">Notifications</h3>
          {unreadCount > 0 && (
            <Button data-testid="mark-all-read-btn" variant="ghost" size="sm" onClick={markAllRead}
              className="text-xs text-court-orange hover:text-court-orange-hover h-7 px-2">
              <CheckCheck className="w-3 h-3 mr-1" />Mark all read
            </Button>
          )}
        </div>

        {/* List */}
        <ScrollArea className="max-h-[400px]">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-court-orange" /></div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-10 px-4">
              <Bell className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No notifications yet</p>
              <p className="text-xs text-muted-foreground mt-1">You'll see booking updates here</p>
            </div>
          ) : (
            <div>
              {notifications.map(n => {
                const Icon = typeIcons[n.type] || Bell;
                const color = typeColors[n.type] || "text-muted-foreground";
                return (
                  <button key={n.notification_id} data-testid={`notif-${n.notification_id}`}
                    onClick={() => handleClick(n)}
                    className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-slate-50 transition-colors border-b border-slate-50 ${!n.is_read ? 'bg-court-orange/[0.03]' : ''}`}>
                    <div className={`mt-0.5 shrink-0 ${color}`}><Icon className="w-4 h-4" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm font-medium ${!n.is_read ? 'text-foreground' : 'text-muted-foreground'}`}>{n.title}</p>
                        {!n.is_read && <span className="w-2 h-2 bg-court-orange rounded-full shrink-0" />}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                      <p className="text-[10px] text-muted-foreground/60 mt-1">{formatTime(n.created_at)}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {notifications.length > 0 && (
          <>
            <DropdownMenuSeparator className="m-0" />
            <div className="p-2 text-center">
              <Button data-testid="view-all-notifications-btn" variant="ghost" size="sm"
                onClick={() => { setOpen(false); navigate("/notifications"); }}
                className="text-xs text-court-orange hover:text-court-orange-hover font-heading uppercase w-full">
                View All Notifications
              </Button>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
